import { useEffect, useState, type ReactNode } from 'react';
import { CircleMarker, Popup } from 'react-leaflet';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { GeoMap, resolveTiles } from './GeoMap.js';
import type { MapConfig } from './geoApi.js';

interface DensityCell {
  latBucket: number;
  lngBucket: number;
  weight: number;
  distinctMediumCount: number;
  distinctVendorCount: number;
}
interface DensityResult {
  precision: number;
  approxCellSizeDegrees: number;
  cellCount: number;
  maxWeight: number;
  cells: DensityCell[];
}

function heatColor(ratio: number): string {
  // 0 -> blue, 1 -> red
  const hue = 240 - Math.round(240 * Math.min(1, Math.max(0, ratio)));
  return `hsl(${hue}, 85%, 50%)`;
}

/** §10 Layers 5-6 heat / density map. */
export function DensityHeatScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [mapConfig, setMapConfig] = useState<MapConfig | null>(null);
  const [density, setDensity] = useState<DensityResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: MapConfig }>('/v1/geo-intel/map-config', { token }),
      apiRequest<{ data: DensityResult }>('/v1/geo-intel/density', { token }),
    ])
      .then(([mc, d]) => { if (!alive) return; setMapConfig(mc.data); setDensity(d.data); setError(null); })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof ApiError && e.status === 403
          ? t('m16report.density.forbidden')
          : (e as Error).message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const center: [number, number] = mapConfig ? [mapConfig.defaultCenter.lat, mapConfig.defaultCenter.lng] : [22.9734, 78.6569];
  const zoom = mapConfig?.defaultZoom ?? 5;
  const tiles = resolveTiles(mapConfig);
  const maxWeight = density?.maxWeight ?? 1;

  return (
    <main aria-label="density-heat">
      <h1>{t('m16report.density.title')}</h1>
      {error && <p role="alert" aria-label="density-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          {density && (
            <p aria-label="density-summary">
              {t('m16report.density.summary', { cells: density.cellCount, weight: density.maxWeight, size: density.approxCellSizeDegrees })}
            </p>
          )}
          <GeoMap center={center} zoom={zoom} tiles={tiles} ariaLabel="density-map">
            {(density?.cells ?? []).map((c) => (
              <CircleMarker
                key={`${c.latBucket}:${c.lngBucket}`}
                center={[c.latBucket, c.lngBucket]}
                radius={6 + Math.round(14 * (c.weight / (maxWeight || 1)))}
                pathOptions={{ color: heatColor(c.weight / (maxWeight || 1)), fillOpacity: 0.5, weight: 1 }}
              >
                <Popup>
                  <div aria-label={`cell-${c.latBucket}-${c.lngBucket}`}>
                    {t('m16report.density.popup.ads', { ads: c.weight })}<br />{t('m16report.density.popup.mediums', { mediums: c.distinctMediumCount })}<br />{t('m16report.density.popup.vendors', { vendors: c.distinctVendorCount })}
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </GeoMap>
        </>
      )}
    </main>
  );
}
